
// src/services/CacheService.js

// Safe localStorage wrapper with expiry support
const CACHE_PREFIX = 'NEO_CACHE_';
const DEFAULT_EXPIRY_MINUTES = 30; // 30 minutes default

// RAM Store for sensitive/heavy keys (inputs, history) to avoid LocalStorage pollution
// These will persist during the session but vanish on reload.
const RAM_STORE = new Map();

class CacheService {
    /**
     * Set data in localStorage with expiry
     * @param {string} key - Cache key
     * @param {any} value - Data to simplify
     * @param {number} expiryMinutes - Time to live in minutes
     */
    static set(key, value, expiryMinutes = DEFAULT_EXPIRY_MINUTES) {
        // 1. RAM Diversion for specific keys (Privacy/Cleanliness)
        // User requested 'inputs' and 'history' to NOT be in localStorage
        if (key.startsWith('inputs_') || key.startsWith('history_')) {
            const now = new Date();
            const item = {
                value: value,
                expiry: now.getTime() + (expiryMinutes * 60 * 1000)
            };
            RAM_STORE.set(key, item);
            // Ensure it's REMOVED from disk if it was there previously (Cleanup)
            localStorage.removeItem(CACHE_PREFIX + key);
            return;
        }

        try {
            const now = new Date();
            const item = {
                value: value,
                expiry: now.getTime() + (expiryMinutes * 60 * 1000),
                timestamp: now.getTime()
            };
            localStorage.setItem(CACHE_PREFIX + key, JSON.stringify(item));
        } catch (error) {
            console.warn('Cache write failed:', error);
        }
    }

    /**
     * Get data from localStorage if valid
     * @param {string} key - Cache key
     * @returns {any|null} Cached value or null
     */
    static get(key) {
        // 1. Check RAM first
        if (RAM_STORE.has(key)) {
            const item = RAM_STORE.get(key);
            if (Date.now() > item.expiry) {
                RAM_STORE.delete(key);
                return null;
            }
            return item.value;
        }

        try {
            const itemStr = localStorage.getItem(CACHE_PREFIX + key);
            if (!itemStr) return null;

            const item = JSON.parse(itemStr);
            const now = new Date();

            // Check expiry
            if (now.getTime() > item.expiry) {
                localStorage.removeItem(CACHE_PREFIX + key);
                return null;
            }

            return item.value;
        } catch (error) {
            console.warn('Cache read failed:', error);
            return null;
        }
    }

    /**
     * Remove item from cache
     * @param {string} key 
     */
    static remove(key) {
        RAM_STORE.delete(key);
        localStorage.removeItem(CACHE_PREFIX + key);
    }

    /**
     * Clear all NEO_CACHE items
     */
    static clearAll() {
        RAM_STORE.clear();
        Object.keys(localStorage).forEach(key => {
            if (key.startsWith(CACHE_PREFIX)) {
                localStorage.removeItem(key);
            }
        });
    }
}

export default CacheService;
